'use client';
import React, { useState, useEffect, useContext } from 'react';
import NextLink from 'next/link';
import {
  ExpandLess,
  ExpandMore,
  ExpandMoreRounded,
  LogoutRounded,
  Menu as MenuIcon,
} from '@mui/icons-material';
import {
  AppBar,
  Avatar,
  Button,
  Collapse,
  IconButton,
  Link,
  List,
  ListItemButton,
  Menu,
  MenuItem,
  MenuList,
  Paper,
  Toolbar,
  Typography,
} from '@mui/material';
import { useCurrentPath } from '@/shared/hooks';
import { useRouter } from 'next/navigation';
import { headerLinks } from '@/shared/utils';
import { AuthContext } from '@/auth';

const Header: React.FC = () => {
  const router = useRouter();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentPath, setCurrentPath] = useState<string>('');
  const [, isCurrentPath] = useCurrentPath();
  const open = Boolean(anchorEl);
  const { user, profile } = useContext(AuthContext);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const { pathname } = window.location;
      setCurrentPath(pathname);
    }
  }, []);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMobileClick = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleLogout = () => {
    // setUser(null); // Clear user context on logout
    handleClose();
    setMobileMenuOpen(false); // Close mobile menu after logout
  };

  const links = headerLinks(user !== null, profile?.subscriber);

  const handleLinkClick = (href: string) => {
    router.push(href); // Navigate to the link
    setMobileMenuOpen(false); // Close the mobile menu
  };

  const userAvatar = {
    src: 'https://images.unsplash.com/photo-1508341591423-4347099e1f19?q=80&w=3265&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    name: user ? profile?.name : 'Guest', // Only use user.name if authenticated
  };

  return (
    <>
      {/* Desktop Header */}
      <div className="hidden lg:block">
        <header className="flex justify-between items-center px-8 py-6 gap-x-4 w-full bg-paper z-10 h-[90px]">
          <NextLink href="/">
            <img
              loading="lazy"
              src="assets/images/logotipo-white.svg"
              alt="Vibly"
              className="h-[42px] w-auto"
            />
          </NextLink>
          <div className="flex gap-x-4">
            {links.map((link) => (
              <NextLink
                key={link.href}
                style={{ textDecoration: 'none' }}
                target={link.label.toLowerCase().includes('community') ? '_blank' : '_self'}
                href={link.href}
              >
                <Typography
                  sx={{
                    color: isCurrentPath(link.href) ? 'primary.main' : 'primary',
                    fontWeight: 'bold',
                    fontSize: '1rem',
                    letterSpacing: '0.5px',
                  }}
                >
                  {link.label}
                </Typography>
              </NextLink>
            ))}
          </div>
          <div>
            {user ? ( // Check if user is authenticated
              <div className="flex items-center gap-x-4">
                <Typography variant="body-400">{profile?.name}</Typography>{' '}
                <Avatar
                  sizes="large"
                  alt={userAvatar.name}
                  src={userAvatar.src}
                  sx={{ border: '1px solid #FBE34E' }}
                />
                <div>
                  <IconButton
                    aria-label="more"
                    id="long-button"
                    aria-controls={open ? 'long-menu' : undefined}
                    aria-expanded={open ? 'true' : undefined}
                    aria-haspopup="true"
                    onClick={handleClick}
                  >
                    <ExpandMoreRounded className="text-white" />
                  </IconButton>
                  <Menu
                    id="long-menu"
                    MenuListProps={{ 'aria-labelledby': 'long-button' }}
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                  >
                    <MenuItem onClick={handleLogout}>Logout</MenuItem>
                  </Menu>
                </div>
              </div>
            ) : (
              <div className="flex gap-x-4">
                <NextLink href="/auth/login">
                  <Button component="span" size="small" variant="outlined" color="primary">
                    <Typography variant="button-m">Accedi</Typography>
                  </Button>
                </NextLink>
                <NextLink href="/auth/registrazione">
                  <Button component="span" size="small" variant="contained" color="primary">
                    <Typography variant="button-m">Iscriviti</Typography>
                  </Button>
                </NextLink>
              </div>
            )}
          </div>
        </header>
      </div>

      <div className="lg:hidden">
        <AppBar position="fixed" className="h-[60px] sm:h-[70px]">
          <Toolbar className="flex justify-between items-center">
            <img
              loading="lazy"
              src="/assets/images/logotipo-white.svg"
              alt="Vibly"
              className="h-[35px] w-auto"
            />
            <IconButton edge="start" aria-label="menu" onClick={handleMobileClick}>
              <MenuIcon className="text-text-primary" />
            </IconButton>
            {mobileMenuOpen && (
              <Paper
                square={true}
                className="fixed top-[60px] sm:top-[70px] left-0 w-full h-full rounded-none"
              >
                <div className="flex flex-col h-full pb-[60px] sm:pb-[70px] px-2">
                  <MenuList className="w-full pt-6 space-y-2 flex-grow overflow-auto">
                    {headerLinks(!!user?.email).map((link) => (
                      <MenuItem key={link.href} onClick={() => handleLinkClick(link.href)}>
                        <Link
                          key={link.href}
                          underline="none"
                          color={isCurrentPath(link.href) ? 'primary' : 'contrastText'}
                          variant="button-l"
                          target={
                            link.label.toLowerCase().includes('community') ? '_blank' : '_self'
                          }
                          href={link.href}
                        >
                          {link.label}
                        </Link>
                      </MenuItem>
                    ))}
                    {user && (
                      <>
                        <MenuItem onClick={handleClick}>
                          <Link underline="none" variant="button-l" color="textPrimary">
                            <span className="pr-2">Il tuo profilo</span>
                            {open ? <ExpandLess /> : <ExpandMore />}
                          </Link>
                        </MenuItem>
                        <Collapse in={open} timeout="auto" unmountOnExit>
                          <List component="div" className="space-y-1">
                            <ListItemButton onClick={() => handleLinkClick('/profilo/me')}>
                              <Link
                                underline="none"
                                color={isCurrentPath('/profilo/me') ? 'primary' : 'textPrimary'}
                                variant="button-l"
                              >
                                Profilo
                              </Link>
                            </ListItemButton>
                            <ListItemButton onClick={() => handleLinkClick('/profilo/strumenti')}>
                              <Link
                                underline="none"
                                color={
                                  isCurrentPath('/profilo/strumenti') ? 'primary' : 'textPrimary'
                                }
                                variant="button-l"
                              >
                                Strumenti
                              </Link>
                            </ListItemButton>
                            <ListItemButton
                              onClick={() => handleLinkClick('/profilo/sessioni-studio')}
                            >
                              <Link
                                underline="none"
                                color={
                                  isCurrentPath('/profilo/sessioni-studio')
                                    ? 'primary'
                                    : 'textPrimary'
                                }
                                variant="button-l"
                              >
                                Sessioni
                              </Link>
                            </ListItemButton>
                            <ListItemButton onClick={() => handleLinkClick('/profilo/acquisti')}>
                              <Link
                                underline="none"
                                color={
                                  isCurrentPath('/profilo/acquisti') ? 'primary' : 'textPrimary'
                                }
                                variant="button-l"
                              >
                                Acquisti
                              </Link>
                            </ListItemButton>
                            <ListItemButton
                              onClick={() => handleLinkClick('/profilo/cambia-password')}
                            >
                              <Link
                                underline="none"
                                color={
                                  isCurrentPath('/profilo/cambia-password')
                                    ? 'primary'
                                    : 'textPrimary'
                                }
                                variant="button-l"
                              >
                                Cambia password
                              </Link>
                            </ListItemButton>
                          </List>
                        </Collapse>
                      </>
                    )}
                  </MenuList>
                  {user ? (
                    <div className="px-4 py-6">
                      <div className="rounded-lg bg-background flex gap-4 pl-4 pr-2 py-2 justify-between">
                        <div className="flex items-center gap-4">
                          <Avatar
                            sizes="large"
                            alt={userAvatar.name}
                            src={userAvatar.src}
                            sx={{ height: '40px' }}
                          />
                          <Typography variant="body-400">{userAvatar.name}</Typography>
                        </div>
                        <IconButton edge="start" aria-label="menu" size="large">
                          <LogoutRounded sx={{ height: '18px', width: '18px' }} />
                        </IconButton>
                      </div>
                    </div>
                  ) : (
                    <div className="px-4 py-6 flex flex-col gap-4">
                      <Button variant="outlined" color="primary" size="medium">
                        <Typography variant="button-m">Login</Typography>
                      </Button>
                      <Button variant="contained" color="primary" size="medium">
                        <Typography variant="button-m">Sign Up</Typography>
                      </Button>
                    </div>
                  )}
                </div>
              </Paper>
            )}
          </Toolbar>
        </AppBar>
      </div>
    </>
  );
};

export default Header;
